import { type InputHTMLAttributes, useState } from 'react'
import { TextField } from '../ui/TextField'
import { SettingEditButton, SettingForm, SettingRow, useSettingEditor } from './Setting'

interface EditableSettingProps {
  label: string
  value: string
  /** The accessible name of the Edit control, such as "Edit your name". */
  editLabel: string
  save(value: string): Promise<unknown>
  validate?(value: string): string | null
  field?: Pick<InputHTMLAttributes<HTMLInputElement>, 'autoComplete' | 'autoCapitalize'>
  testId: string
}

/** A single text value, shown at rest and edited in place. */
export function EditableSetting({
  label,
  value,
  editLabel,
  save,
  validate,
  field,
  testId,
}: EditableSettingProps) {
  const [draft, setDraft] = useState(value)
  const editor = useSettingEditor({
    save: () => save(draft.trim()),
    validate: () => validate?.(draft.trim()) ?? null,
  })

  // The draft starts from the current value each time, never from a cancelled edit.
  function start() {
    setDraft(value)
    editor.start()
  }

  return (
    <div data-testid={testId}>
      {editor.open ? (
        <SettingForm editor={editor} submitLabel="Save">
          <TextField
            {...field}
            label={label}
            labelStyle="floating"
            required
            invalid={editor.error !== null}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            data-testid="setting-input"
          />
        </SettingForm>
      ) : (
        <SettingRow
          label={label}
          value={value}
          action={
            <SettingEditButton editor={editor} onClick={start} aria-label={editLabel}>
              Edit
            </SettingEditButton>
          }
        />
      )}
    </div>
  )
}
